import React, { useRef } from 'react';
import { useContext } from 'react';
import { useEmailState } from 'components/EmailStateProvider';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import CrmEmailDiscardPopup from 'components/Dashboard/Influence/CrmEmails/CrmEmailDiscardPopup/CrmEmailDiscardPopup';
import CrmEmailComponentSkeleton from './CrmEmailComponentSkeleton';
import EmailComponentBody from './EmailComponentBody';
import { useLocation } from 'react-router-dom';
import { applyEmailSignatureToBody } from './emailSignatureFunctions';
import { useGroupFunctions } from 'components/Dashboard/Influence/CrmGroups/GroupFunctions';
import useCallRemainingEmailCampaigns from 'components/Dashboard/Influence/CrmEmails/useCallRemainingEmailCampaigns';
import LocalStorage from 'utils/LocalStorage';
import useEmailComponentEffects from './useEmailComponentEffects';
import useEmailComponentViewConditions from './useEmailComponentViewConditions';
import { resolveEmailRouteState } from './emailRouteState';

const EmailComponent = ({ emailId }) => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { listDomainsVerified, team, activePlan } = state;

  const { emailState, setEmailState } = useEmailState();
  const { currentEmailDetails, showSendPreviewPopUp } = emailState;

  const { contentResults } = useContentResults();
  const { subject } = currentEmailDetails;
  const { Groups } = contentResults ?? {};

  const emailAddresses = listDomainsVerified?.verifiedEmailAddresses;
  const location = useLocation();
  const routeState = resolveEmailRouteState({ location, defaultTab: 'Email' });

  const { subscriptionStatus } = activePlan;
  const trialUser = subscriptionStatus === 'InTrial';

  const { listingGroups } = useGroupFunctions();
  const { callRemainingEmailCampaigns } = useCallRemainingEmailCampaigns();

  const callGroupsList = useRef(null);
  callGroupsList.current = async (source) => {
    try {
      if (!Groups) {
        await listingGroups({ source, includeCounts: true });
      }
      await callRemainingEmailCampaigns(source);
    } catch (error) {}
  };
  const {
    isLoading,
    isLoadingWritingAssistant,
    editorOnLoad,
    setEditorOnLoad,
    handleDiscardNavigation,
    showLeavePopUp,
    setShowLeavePopUp,
    refreshEmailData,
    autofocusFieldFromUrl,
    emailContainerRef,
  } = useEmailComponentEffects({
    emailId,
    callGroupsList,
    dispatch,
    listDomainsVerified,
    emailAddresses,
  });

  const handleFrom = (item) => {
    let newCurrentEmailDetails = {
      ...currentEmailDetails,
      senderEmailAddressId: item?.id,
      senderDisplayName: item?.displayName,
    };
    newCurrentEmailDetails.body = applyEmailSignatureToBody({
      emailAddressItem: item,
      emailBody: currentEmailDetails.body,
    });

    setEmailState('currentEmailDetails', newCurrentEmailDetails);

    const lastSenderEmailAddress = {
      account: team?.myself.emailAddress,
      emailAddressId: item?.id,
    };
    LocalStorage.setItem('lastSenderEmailAddress', JSON.stringify(lastSenderEmailAddress));
  };

  const handleSubject = (event) => {
    setEmailState('currentEmailDetails', {
      ...currentEmailDetails,
      subject: event.target.value,
      title: event.target.value,
    });
  };

  const handleBody = (value) => {
    setEmailState('currentEmailDetails', { ...currentEmailDetails, body: value, subject });
  };

  const initialToValue = () => {
    const prepopulatedContacts = routeState.prepopulatedContacts;
    if (prepopulatedContacts) {
      return [...prepopulatedContacts].sort((a, b) => {
        if (a.label[0] > b.label[0]) return 1;
        if (a.label[0] < b.label[0]) return -1;
        return 0;
      });
    } else if (currentEmailDetails.sendRecords.length > 0) {
      return currentEmailDetails.sendRecords.map((record) => {
        return {
          value: record.crmContactId,
          imageId: record.imageId,
          label: record.fullName,
          id: record.crmContactId,
          email: record.emailAddress,
        };
      });
    } else {
      return [];
    }
  };

  const {
    sentEmail,
    disabledToCondition,
    bodyCondition,
    skeletonCondition,
  } = useEmailComponentViewConditions({
    currentEmailDetails,
    isLoading,
    listDomainsVerified,
    location,
    editorOnLoad,
  });

  const handleMailMergeData = (value) => {
    setEmailState('currentEmailDetails', { ...currentEmailDetails, hasMailMergeData: value });
  };

  return (
    <>
      <CrmEmailDiscardPopup
        setShowPopup={setShowLeavePopUp}
        discardFunction={handleDiscardNavigation}
        showPopUp={showLeavePopUp}
      />
      <div
        className={`${sentEmail ? 'main-content-sent-email' : 'main-content px-md-4 pr-xl-5'} px-md-4 px-3 h-100 email-component-wrapper`}
        ref={emailContainerRef}
      >
        <div className={`d-flex flex-column h-100 ${sentEmail ? '' : 'pt-2'}`}>
          <div className='mx-0 h-100'>
            <div className='flex-grow-1 h-100 position-relative' style={isLoading ? { overflow: 'hidden' } : null}>
              {bodyCondition ? (
                <div className={sentEmail ? `email-component-body-sent h-100` : 'h-100'}>
                  <EmailComponentBody
                    currentEmailDetails={currentEmailDetails}
                    handleFrom={handleFrom}
                    handleMailMergeData={handleMailMergeData}
                    disabledCondition={sentEmail}
                    disabledToCondition={disabledToCondition}
                    handleSubject={handleSubject}
                    handleBody={handleBody}
                    initialToValue={initialToValue}
                    emailAddresses={emailAddresses}
                    autofocusFieldFromUrl={autofocusFieldFromUrl}
                    setEditorOnLoad={setEditorOnLoad}
                    trialUser={trialUser}
                    setActiveTab={() => {}}
                    setActiveRecipientsOption={() => {}}
                    skeletonCondition={skeletonCondition}
                    isLoadingWritingAssistant={isLoadingWritingAssistant}
                    showSendPreviewPopUp={showSendPreviewPopUp}
                    refreshEmailData={refreshEmailData}
                    emailState={emailState}
                    setEmailState={setEmailState}
                  />
                </div>
              ) : (
                <>
                  <CrmEmailComponentSkeleton />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailComponent;
